import React, { ChangeEvent, ReactNode, useRef } from 'react';
import { Avatar, FormHelperText, Theme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { faCloudUpload } from '@fortawesome/pro-solid-svg-icons/faCloudUpload';
import { makeStyles } from '@mui/styles';
import { uploadFileValidation } from '@loyalty/shared-common/asset-api-client';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api-client/ApiClientProvider';

const useStyles = makeStyles<Theme, { size: number }>(({ shape }) => ({
  root: ({ size }) => ({
    width: size,
  }),
  image: {
    backgroundColor: 'transparent',
    height: '100% !important',
    width: '100% !important',
  },
  icon: ({ size }) => {
    const checkerColor = colorPalette.grey.main;
    return {
      backgroundColor: 'white',
      backgroundImage: `
        linear-gradient(45deg, ${checkerColor} 25%, transparent 25%),
        linear-gradient(-45deg, ${checkerColor} 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, ${checkerColor} 75%),
        linear-gradient(-45deg, transparent 75%, ${checkerColor} 75%)
      `,
      backgroundSize: '16px 16px',
      backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
      border: `1px solid ${checkerColor}`,
      borderRadius: shape.borderRadius,
      cursor: 'pointer',
      flex: `0 0 ${size}px`,
      height: size,
      overflow: 'hidden',
      position: 'relative',
      width: size,

      '& img': {
        height: '100%',
        width: '100%',
        objectFit: 'contain',
      },
    };
  },
  fileInput: {
    display: 'none',
  },
}));

interface EventLike {
  target: {
    name: string;
    value: string;
  };
}

interface Props {
  name: string;
  type: string;
  programId: string;
  value: string;
  onChange?: (event: EventLike) => void;
  errorMessage?: ReactNode;
  onError?: (name: string, message?: string) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  size?: number;
}

export function UploadableImage({
  children,
  programId,
  value,
  name,
  type,
  disabled,
  onChange = () => null,
  errorMessage,
  onError = () => null,
  size = 175,
}: Props): React.ReactElement {
  const { i18n } = useTranslation();
  const classes = useStyles({ size });

  const { assetApi } = useApi();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { files, name } = target;
    const file = files?.[0];

    if (!file) {
      return;
    }

    const [isValid, error] = await uploadFileValidation(file, i18n);

    if (!isValid) {
      onError(name, error);
      return;
    }

    try {
      const { url } = await assetApi.uploadFile(programId, type, file);

      onChange({ target: { name, value: url } });
    } catch (err) {
      onError(name, (err as any).message);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.icon} onClick={handleSelectFile}>
        <input
          type="file"
          name={name}
          accept=".jpg,.jpeg,.png"
          ref={fileInputRef}
          onChange={handleFileChange}
          className={classes.fileInput}
          disabled={disabled}
        />
        <Avatar className={classes.image} variant="square" src={value}>
          {children || <FontAwesomeIcon icon={faCloudUpload} size="4x" />}
        </Avatar>
      </div>

      {!!errorMessage && (
        <FormHelperText error>
          <span>{errorMessage}</span>
        </FormHelperText>
      )}
    </div>
  );
}
